import { Chip } from '@vnetwork/ui/primitives';
import type { TimelineItem } from '@vnetwork/ui/data';
import { AccountAvatar } from '@/pages/account/components/AccountAvatar';
import type { ActivityLogRow } from '@/api/customer-activity';
import { ActivityMetadataDiff } from './activity-metadata-diff';

/**
 * Maps a customer activity log row to a @ui TimelineItem. Rich content (avatar,
 * action chip, metadata diff) is preserved via ReactNode slots.
 */

/** action_type → EN label. Unknown values fall back to a humanized string. */
const ACTION_LABELS: Record<string, string> = {
  classify_lead: 'Lead classified',
  'UPDATE-CONTACT': 'Contact updated',
  'CREATED-CUSTOMER-JOURNEY': 'Journey created',
  'CREATED-CUSTOMER': 'Customer created',
  'VIEW-CUSTOMER': 'Viewed',
};

function actionLabel(action: string): string {
  if (ACTION_LABELS[action]) return ACTION_LABELS[action];
  const words = action.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** ISO datetime → local "HH:MM". */
function formatTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Strip any HTML tags the backend may embed in `description`. */
function stripHtml(html: string): string {
  return html ? html.replace(/<[^>]*>/g, '').trim() : '';
}

/** Convert an ActivityLogRow to a @ui TimelineItem. */
export function activityLogToTimelineItem(row: ActivityLogRow): TimelineItem {
  const actor = row.employee_name?.trim() || 'System';
  const description = stripHtml(row.description);

  return {
    id: row.log_id,
    // Title: inline actor identity + action label
    title: (
      <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
        <AccountAvatar name={actor} size={24} />
        <span style={{ fontWeight: 'var(--vn-font-weight-semibold)', fontSize: 'var(--vn-font-size-sm)' }}>
          {actor}
        </span>
        <Chip tone="neutral" appearance="subtle" size="sm">
          {actionLabel(row.action_type)}
        </Chip>
      </span>
    ),
    timestamp: (
      <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-subtle)', fontVariantNumeric: 'tabular-nums' }}>
        {formatTime(row.created_at)}
      </span>
    ),
    // description: note text + rich metadata diff (both optional)
    description: (description || row.metadata) ? (
      <div>
        {description && (
          <p style={{ margin: 0, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
            {description}
          </p>
        )}
        <ActivityMetadataDiff metadata={row.metadata} />
      </div>
    ) : undefined,
    tone: 'neutral',
  };
}

export default activityLogToTimelineItem;
